import { graphql } from "@__generated__/gql";

export const LOGIN_MUTATION = graphql(`
  mutation Login($email: String!, $password: String!) {
    authenticateUserWithPassword(email: $email, password: $password) {
      ... on UserAuthenticationWithPasswordFailure {
        message
      }
      ... on UserAuthenticationWithPasswordSuccess {
        item {
          id
          email
        }
        sessionToken
      }
    }
  }
`);

export const UPDATE_PASSWORD_MUTATION = graphql(`
  mutation UpdateUserPassword($where: UserWhereUniqueInput!, $data: UserUpdateInput!) {
    updateUser(where: $where, data: $data) {
      id
      email
    }
  }
`);

export const UPDATE_USER_MUTATION = graphql(`
  mutation UpdateUserInformation($where: UserWhereUniqueInput!, $data: UserUpdateInput!) {
    updateUser(where: $where, data: $data) {
      id
      email
      firstName
      lastName
      title
      credentials
      avatarUrl
    }
  }
`);

export const MAGIC_LINK_MUTATION = graphql(`
  mutation SendMagicLink($email: String!) {
    sendUserMagicAuthLink(email: $email)
  }
`);

export const CREATE_USER_MUTATION = graphql(`
  mutation CreateUser($data: UserCreateInput!) {
    createUser(data: $data) {
      id
      email
    }
  }
`);

export const VERIFY_MAGIC_LINK_MUTATION = graphql(`
  mutation VerifyMagicToken($email: String!, $token: String!) {
    redeemUserMagicAuthToken(email: $email, token: $token) {
      ... on RedeemUserMagicAuthTokenFailure {
        code
        message
      }
      ... on RedeemUserMagicAuthTokenSuccess {
        token
      }
    }
  }
`);

export const CREATE_REPORT_MUTATION = graphql(`
  mutation CreateReport($data: ReportCreateInput!) {
    createReport(data: $data) {
      id
      code
    }
  }
`);

export const UPDATE_REPORT_MUTATION = graphql(`
  mutation UpdateReport($where: ReportWhereUniqueInput!, $data: ReportUpdateInput!) {
    updateReport(where: $where, data: $data) {
      id
      code
      insurer
      broker
      stage {
        id
        name
      }
      status {
        id
        name
      }
      assessor {
        id
        firstName
        lastName
        email
      }
      reportType {
        id
        name
      }
      weatherCondition
      weatherLeadInDays
      evidence
      attendees
    }
  }
`);

export const CREATE_FIELD_WORK_MUTATION = graphql(`
  mutation CreateFieldWork($data: FieldWorkCreateInput!) {
    createFieldWork(data: $data) {
      id
      siteAccessAndSafety
      initialSiteObservations
      preliminaryFireAssessment
    }
  }
`);

export const UPDATE_FIELD_WORK_MUTATION = graphql(`
  mutation UpdateFieldWork($where: FieldWorkWhereUniqueInput!, $data: FieldWorkUpdateInput!) {
    updateFieldWork(where: $where, data: $data) {
      id
      siteAccessAndSafety
      initialSiteObservations
      preliminaryFireAssessment
    }
  }
`);

export const UPDATE_PROPERTY_INFORMATION_MUTATION = graphql(`
  mutation UpdatePropertyInformation(
    $where: PropertyInformationWhereUniqueInput!
    $data: PropertyInformationUpdateInput!
  ) {
    updatePropertyInformation(where: $where, data: $data) {
      id
      riskAddress
      inspectionAddress
    }
  }
`);

export const UPDATE_REPORT_REFERENCE_MUTATION = graphql(`
  mutation UpdateReportReference(
    $where: ReportReferenceWhereUniqueInput!
    $data: ReportReferenceUpdateInput!
  ) {
    updateReportReference(where: $where, data: $data) {
      id
      jobNumber
      claimNumber
      refNumber
    }
  }
`);

export const CREATE_REPORT_REFERENCE_MUTATION = graphql(`
  mutation CreateReportReference($data: ReportReferenceCreateInput!) {
    createReportReference(data: $data) {
      id
      jobNumber
      claimNumber
      refNumber
    }
  }
`);

export const UPLOAD_REPORT_PHOTO_MUTATION = graphql(`
  mutation UploadReportPhoto($reportId: ID!, $file: Upload!) {
    uploadReportPhoto(reportId: $reportId, file: $file) {
      id
      url
      filename
      originalName
    }
  }
`);

export const END_SESSION_MUTATION = graphql(`
  mutation EndSession {
    endSession
  }
`);

export const UPDATE_REPORT_SUMMARY_MUTATION = graphql(`
  mutation UpdateReportSummary(
    $where: ReportSummaryWhereUniqueInput!
    $data: ReportSummaryUpdateInput!
  ) {
    updateReportSummary(where: $where, data: $data) {
      id
      executiveSummary
      scope
      scopeItems
      keyConclusions
      recommendations
    }
  }
`);

export const CREATE_REPORT_SUMMARY_MUTATION = graphql(`
  mutation CreateReportSummary($data: ReportSummaryCreateInput!) {
    createReportSummary(data: $data) {
      id
      executiveSummary
      scope
      scopeItems
      keyConclusions
      recommendations
    }
  }
`);

export const UPDATE_ANALYSIS_MUTATION = graphql(`
  mutation UpdateAnalysis($where: AnalysisWhereUniqueInput!, $data: AnalysisUpdateInput!) {
    updateAnalysis(where: $where, data: $data) {
      id
      circumstanceSummary
      observation
      narrative
      otherFactorsConsidered
    }
  }
`);

export const CREATE_ANALYSIS_MUTATION = graphql(`
  mutation CreateAnalysis($data: AnalysisCreateInput!) {
    createAnalysis(data: $data) {
      id
      circumstanceSummary
      observation
      narrative
      otherFactorsConsidered
    }
  }
`);

export const UPDATE_SETTING_MUTATION = graphql(`
  mutation UpdateSetting($where: SettingWhereUniqueInput!, $data: SettingUpdateInput!) {
    updateSetting(where: $where, data: $data) {
      id
      defaultWeatherLeadInDays
    }
  }
`);

export const CREATE_WITNESS_INTERVIEW_MUTATION = graphql(`
  mutation CreateWitnessInterview($data: WitnessInterviewCreateInput!) {
    createWitnessInterview(data: $data) {
      id
      name
      witnessRelationship
      event
      interviewDatetime
      briefNote
      detailedNote
    }
  }
`);

export const UPDATE_WITNESS_INTERVIEW_MUTATION = graphql(`
  mutation UpdateWitnessInterview(
    $where: WitnessInterviewWhereUniqueInput!
    $data: WitnessInterviewUpdateInput!
  ) {
    updateWitnessInterview(where: $where, data: $data) {
      id
      name
      witnessRelationship
      event
      interviewDatetime
      briefNote
      detailedNote
    }
  }
`);

export const DELETE_WITNESS_INTERVIEW_MUTATION = graphql(`
  mutation DeleteWitnessInterview($where: WitnessInterviewWhereUniqueInput!) {
    deleteWitnessInterview(where: $where) {
      id
    }
  }
`);

export const CREATE_REPORT_REVIEW_MUTATION = graphql(`
  mutation CreateReportReview($data: ReportReviewCreateInput!) {
    createReportReview(data: $data) {
      ...ReportReviewFields
    }
  }
`);

export const UPDATE_REPORT_REVIEW_MUTATION = graphql(`
  mutation UpdateReportReview(
    $where: ReportReviewWhereUniqueInput!
    $data: ReportReviewUpdateInput!
  ) {
    updateReportReview(where: $where, data: $data) {
      ...ReportReviewFields
    }
  }
`);

export const GENERATE_REPORT_SUMMARY_MUTATION = graphql(`
  mutation GenerateReportSummary($reportId: ID!, $field: SummaryField!) {
    generateReportSummary(reportId: $reportId, field: $field) {
      field
      content
    }
  }
`);

export const BUILD_REPORT_CONCLUSIONS_MUTATION = graphql(`
  mutation BuildReportConclusions($reportId: ID!) {
    buildReportConclusions(reportId: $reportId) {
      field
      content
    }
  }
`);

export const GENERATE_REPORT_PDF_MUTATION = graphql(`
  mutation GenerateReportPdf($reportId: ID!, $type: ReportPdfType!) {
    generateReportPdf(reportId: $reportId, type: $type) {
      fileName
      mimeType
      base64
    }
  }
`);

export const SEND_REPORT_PDF_MUTATION = graphql(`
  mutation SendReportPdf(
    $reportId: ID!
    $type: ReportPdfType!
    $clientEmail: String
    $recipientEmails: [String!]
  ) {
    sendReportPdf(
      reportId: $reportId
      type: $type
      clientEmail: $clientEmail
      recipientEmails: $recipientEmails
    ) {
      success
      recipientCount
    }
  }
`);

export const GENERATE_ANALYSIS_NARRATIVE_MUTATION = graphql(`
  mutation GenerateAnalysisNarrative(
    $reportId: ID!
    $observationData: String
    $circumstanceSummary: String
    $maxWords: Int
  ) {
    generateAnalysisNarrative(
      reportId: $reportId
      observationData: $observationData
      circumstanceSummary: $circumstanceSummary
      maxWords: $maxWords
    ) {
      content
    }
  }
`);

export const MARK_REPORT_READY_FOR_REVIEW_MUTATION = graphql(`
  mutation MarkReportReadyForReview($reportId: ID!) {
    markReportReadyForReview(reportId: $reportId) {
      id
      code
      client
      stage {
        id
        name
      }
      emailsFailed
      notifiedReviewerIds
    }
  }
`);

export const NOTIFY_NEW_REVIEWERS_MUTATION = graphql(`
  mutation NotifyNewReviewers($reportId: ID!) {
    notifyNewReviewers(reportId: $reportId) {
      emailsFailed
      notifiedReviewerIds
    }
  }
`);

export const MARK_REPORT_COMPLETE_MUTATION = graphql(`
  mutation MarkReportComplete($reportId: ID!) {
    markReportComplete(reportId: $reportId) {
      id
      code
      client
      stage {
        id
        name
      }
    }
  }
`);

export const ADD_SETTING_OPTION_MUTATION = graphql(`
  mutation AddSettingOption($field: SettingOptionField!, $value: String!) {
    addSettingOption(field: $field, value: $value) {
      value
      label
    }
  }
`);
